// <yoj-megamenu>: the yoj menu bar (snippets/yoj-megamenu.liquid). An item's panel opens when the pointer rests on it
// (a short pause, so sweeping across the bar doesn't flicker), on a click or tap, or from the keyboard; it closes when
// the pointer leaves (if hover opened it), on Escape, on a click or tap elsewhere, or when focus moves away. Items that
// don't fit across the bar go into More. Keys: Left/Right/Home/End along the bar, Down into a panel, Up/Down/Home/End
// within it, Escape back to its item. A three-level panel lists its groups down the left: the group the pointer rests
// on (a short pause, so moving across to its links doesn't switch groups), the one with focus, or the one tapped first
// shows its links; Up/Down move between groups, Right into the group's links, Left back. Pictures in the panels load
// once the page has settled, or as soon as the pointer reaches their item or group, so a panel doesn't open on an
// empty frame.
const OPEN_DELAY = 150; // ms the pointer rests on an item before its panel opens
const SWITCH_DELAY = 80; // ms on another item before the open panel switches to it
const CLOSE_DELAY = 300; // ms after the pointer leaves before a panel hover opened closes
const GROUP_DELAY = 90; // ms the pointer rests on a group before its links show
const whenIdle = (task) => ('requestIdleCallback' in window ? requestIdleCallback(task, { timeout: 4000 }) : setTimeout(task, 2000));

class YojMegamenu extends HTMLElement {
  #open = null; // the item whose panel is open
  #byHover = false; // whether hover opened it
  #timer = 0;
  #groupTimer = 0;
  #tapToShow = false; // a tap is on a group whose links aren't showing yet
  #watch = null;
  #resize = null;

  connectedCallback() {
    this.#watch = new AbortController();
    const signal = this.#watch.signal;
    this.nav = this.querySelector('.yoj-mega__nav');
    this.bar = this.querySelector('.yoj-mega__bar');
    this.more = this.bar.querySelector(':scope > .yoj-mega__item--more');
    this.items = [...this.bar.children].filter((li) => li !== this.more);
    this.moreGroups = [...this.more.querySelectorAll('.yoj-mega__panel > .yoj-mega__links > .yoj-mega__group')];

    for (const item of [...this.items, this.more]) {
      item.addEventListener('pointerenter', (event) => this.#enter(item, event), { signal });
      item.addEventListener('pointerleave', (event) => this.#leave(item, event), { signal });
      // Focus moving to something outside the item (a null target is a click on nothing, handled below)
      item.addEventListener('focusout', (event) => {
        if (this.#open === item && event.relatedTarget && !item.contains(event.relatedTarget)) this.close();
      }, { signal });
      if (this.#panel(item)) this.#top(item).addEventListener('click', () => this.#click(item), { signal });
    }
    for (const group of this.querySelectorAll('.yoj-mega__group')) {
      if (!this.#groupPanel(group)) continue;
      const link = this.#groupLink(group);
      group.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        clearTimeout(this.#groupTimer);
        this.#warm(group, true);
        this.#groupTimer = setTimeout(() => this.#activate(group), GROUP_DELAY);
      }, { signal });
      group.addEventListener('pointerleave', () => clearTimeout(this.#groupTimer), { signal });
      link.addEventListener('focus', () => this.#activate(group), { signal });
      // A first tap on a group shows its links (a finger can't rest on it); a second follows the link
      link.addEventListener('pointerdown', (event) => {
        this.#tapToShow = event.pointerType === 'touch' && !group.classList.contains('is-active');
      }, { signal });
      link.addEventListener('click', (event) => {
        if (!this.#tapToShow) return;
        this.#tapToShow = false;
        event.preventDefault();
        this.#activate(group);
      }, { signal });
    }
    this.addEventListener('keydown', (event) => this.#key(event), { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.#open && !this.contains(event.target)) this.close();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#open && !this.contains(event.target)) this.close();
    }, { signal });

    this.#resize = new ResizeObserver(() => this.layout());
    this.#resize.observe(this.nav);
    document.fonts?.ready.then(() => this.isConnected && this.layout());
    // Not while the bar is hidden (phones get the theme's drawer): nobody would see the pictures
    const warmAll = () => whenIdle(() => this.checkVisibility?.() !== false && this.#warm(this));
    if (document.readyState === 'complete') warmAll();
    else window.addEventListener('load', warmAll, { once: true, signal });
  }

  disconnectedCallback() {
    this.#watch?.abort();
    this.#resize?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#groupTimer);
  }

  open(item, how) {
    clearTimeout(this.#timer);
    if (this.#open && this.#open !== item) this.#show(this.#open, false);
    this.#open = item;
    this.#byHover = how === 'hover';
    this.#show(item, true);
    this.#prepare(this.#panel(item));
    this.#place(item);
  }

  close({ focus = false } = {}) {
    clearTimeout(this.#timer);
    clearTimeout(this.#groupTimer);
    const item = this.#open;
    if (!item) return;
    this.#open = null;
    this.#show(item, false);
    if (focus) this.#top(item).focus();
  }

  // Puts as many items on the bar as fit, in menu order, and the rest in More
  layout() {
    const width = this.bar.clientWidth;
    if (!width) return; // the bar isn't showing (a narrow window)
    for (const item of this.items) item.hidden = false;
    this.more.hidden = false;
    const sizes = this.items.map((item) => item.getBoundingClientRect().width);
    let fit = this.items.length;
    if (sizes.reduce((sum, size) => sum + size, 0) > width + 0.5) {
      let used = this.more.getBoundingClientRect().width;
      fit = 0;
      while (fit < sizes.length && used + sizes[fit] <= width + 0.5) used += sizes[fit++];
    }
    this.items.forEach((item, i) => {
      item.hidden = i >= fit;
      if (this.moreGroups[i]) this.moreGroups[i].hidden = i < fit;
    });
    this.more.hidden = fit === this.items.length;
    // More is marked when it holds the page (or the section) you're on
    const here = this.items.slice(fit).some((item) => this.#top(item).matches('[aria-current], [data-here]'));
    this.#top(this.more).toggleAttribute('data-here', here);
    if (this.#open?.hidden) this.close();
    else if (this.#open) this.#place(this.#open);
  }

  #top(item) {
    return item.querySelector(':scope > .yoj-mega__top');
  }

  #panel(item) {
    return item.querySelector(':scope > .yoj-mega__panel');
  }

  #groupLink(group) {
    return group.querySelector(':scope > .yoj-mega__group-link');
  }

  #groupPanel(group) {
    return group.querySelector(':scope > .yoj-mega__group-panel');
  }

  #links(item) {
    return [...this.#panel(item).querySelectorAll('a')].filter((link) => !link.closest('[hidden]'));
  }

  #show(item, open) {
    this.#top(item).setAttribute('aria-expanded', String(open));
    this.#panel(item).hidden = !open;
  }

  // Lets lazy pictures load now: those showing when their panel opens, or (all) a group's own
  #warm(scope, all = false) {
    for (const img of scope.querySelectorAll('.yoj-mega__panel img[loading="lazy"]')) {
      if (all || !img.closest('.yoj-mega__group-panel[hidden]')) img.loading = 'eager';
    }
  }

  // A three-level panel opens on its first group, as tall as its tallest group (within the window), so it doesn't
  // jump as the pointer moves down the groups
  #prepare(panel) {
    if (!panel.classList.contains('yoj-mega__panel--deep')) return;
    const groups = [...panel.querySelectorAll('.yoj-mega__group')].filter((group) => this.#groupPanel(group));
    panel.style.removeProperty('--yoj-mega-deep-height');
    const frame = panel.offsetHeight - panel.clientHeight; // the panel's line along the top
    let tallest = panel.querySelector('.yoj-mega__groups').offsetHeight;
    for (const group of groups) {
      const groupPanel = this.#groupPanel(group);
      const wasHidden = groupPanel.hidden;
      groupPanel.hidden = false;
      tallest = Math.max(tallest, groupPanel.offsetHeight);
      groupPanel.hidden = wasHidden;
    }
    const room = Math.max(240, Math.floor(innerHeight - this.nav.getBoundingClientRect().bottom - 16));
    panel.style.setProperty('--yoj-mega-deep-height', `${Math.min(tallest + frame, room)}px`);
    if (groups.length) this.#activate(groups[0]);
  }

  #activate(group) {
    clearTimeout(this.#groupTimer);
    for (const other of group.parentElement.querySelectorAll(':scope > .yoj-mega__group.is-active')) {
      if (other !== group) this.#setGroup(other, false);
    }
    this.#setGroup(group, true);
  }

  #setGroup(group, on) {
    group.classList.toggle('is-active', on);
    this.#groupLink(group).setAttribute('aria-expanded', String(on));
    this.#groupPanel(group).hidden = !on;
  }

  // A panel starts under its item (centred under it on a centred bar), kept inside the bar; every panel fits in the
  // window's height
  #place(item) {
    const panel = this.#panel(item);
    const nav = this.nav.getBoundingClientRect();
    panel.style.setProperty('--yoj-mega-max-height', `${Math.max(240, Math.floor(innerHeight - nav.bottom - 16))}px`);
    if (panel.matches('.yoj-mega__panel--wide, .yoj-mega__panel--deep')) return;
    const top = this.#top(item).getBoundingClientRect();
    const centred = getComputedStyle(this.bar).justifyContent === 'center';
    const start = centred ? top.left + top.width / 2 - panel.offsetWidth / 2 : top.left;
    const left = Math.max(0, Math.min(start - nav.left, nav.width - panel.offsetWidth));
    panel.style.setProperty('--yoj-mega-left', `${Math.round(left)}px`);
  }

  #enter(item, event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return; // a tap opens with its click
    clearTimeout(this.#timer);
    if (this.#open === item) return;
    const panel = this.#panel(item);
    if (!panel && !this.#open) return;
    if (panel) this.#warm(item);
    this.#timer = setTimeout(() => (panel ? this.open(item, 'hover') : this.close()), this.#open ? SWITCH_DELAY : OPEN_DELAY);
  }

  #leave(item, event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    clearTimeout(this.#timer);
    if (this.#open === item && this.#byHover) this.#timer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  #click(item) {
    if (this.#open !== item) return this.open(item, 'click');
    // A click on an item that hover has just opened keeps it open (and a second click closes it)
    if (this.#byHover) {
      clearTimeout(this.#timer);
      this.#byHover = false;
      return;
    }
    this.close();
  }

  #key(event) {
    const { key, target } = event;
    if (key === 'Escape') {
      if (!this.#open) return;
      event.preventDefault();
      this.close({ focus: this.#open.contains(target) });
      return;
    }
    const tops = [...this.items, this.more].filter((item) => !item.hidden).map((item) => this.#top(item));
    const at = tops.indexOf(target);
    if (at >= 0) {
      if (key === 'ArrowRight' || key === 'ArrowLeft') {
        tops[(at + (key === 'ArrowRight' ? 1 : -1) + tops.length) % tops.length].focus();
      } else if (key === 'Home' || key === 'End') {
        tops[key === 'Home' ? 0 : tops.length - 1].focus();
      } else if (key === 'ArrowDown' && this.#panel(target.parentElement)) {
        const item = target.parentElement;
        if (this.#open !== item) this.open(item, 'key');
        this.#links(item)[0]?.focus();
      } else return;
      event.preventDefault();
      return;
    }
    const item = this.#open;
    if (!item || !this.#panel(item).contains(target)) return;
    const panel = this.#panel(item);
    if (panel.classList.contains('yoj-mega__panel--deep')) {
      if (this.#deepKey(event, item, panel)) event.preventDefault();
      return;
    }
    const links = this.#links(item);
    const i = links.indexOf(target);
    if (i < 0) return;
    if (key === 'ArrowDown') links[Math.min(i + 1, links.length - 1)].focus();
    else if (key === 'ArrowUp') (i === 0 ? this.#top(item) : links[i - 1]).focus();
    else if (key === 'Home' || key === 'End') links[key === 'Home' ? 0 : links.length - 1].focus();
    else return;
    event.preventDefault();
  }

  // Keys in a three-level panel: the groups (and the item's "Shop all" below them) as one list, each group's links
  // as another. Returns whether it used the key.
  #deepKey(event, item, panel) {
    const { key, target } = event;
    const heads = [...panel.querySelectorAll('.yoj-mega__group-link, .yoj-mega__shop-all--item')];
    const at = heads.indexOf(target);
    if (at >= 0) {
      if (key === 'ArrowDown') heads[Math.min(at + 1, heads.length - 1)].focus();
      else if (key === 'ArrowUp') (at === 0 ? this.#top(item) : heads[at - 1]).focus();
      else if (key === 'Home' || key === 'End') heads[key === 'Home' ? 0 : heads.length - 1].focus();
      else if (key === 'ArrowRight') {
        const group = target.closest('.yoj-mega__group');
        const first = group && this.#groupPanel(group)?.querySelector('a');
        if (!first) return false;
        first.focus();
      } else return false;
      return true;
    }
    const group = target.closest('.yoj-mega__group');
    const links = group ? [...this.#groupPanel(group).querySelectorAll('a')] : [];
    const i = links.indexOf(target);
    if (i < 0) return false;
    if (key === 'ArrowDown') links[Math.min(i + 1, links.length - 1)].focus();
    else if (key === 'ArrowUp') (i === 0 ? this.#groupLink(group) : links[i - 1]).focus();
    else if (key === 'ArrowLeft') this.#groupLink(group).focus();
    else if (key === 'Home' || key === 'End') links[key === 'Home' ? 0 : links.length - 1].focus();
    else return false;
    return true;
  }
}

if (!customElements.get('yoj-megamenu')) customElements.define('yoj-megamenu', YojMegamenu);
